import { useMutation } from "@apollo/client/react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { match } from "ts-pattern";

import type { PropertySchemaAssociationInput, ValidationError } from "~/common/types";
import {
  GetDataSourceTemplateDetailDocument,
  GetDataSourceTemplateForEditFormDocument,
  GetDataSourceTemplatesDocument,
  UpdateDataSourceTemplateDocument,
  type UpdateDataSourceTemplateMutation,
  type UpdateDataSourceTemplateMutationVariables,
} from "~/features/dataSourceTemplates/gqls";
import type { DataSourceTemplateFormData } from "~/features/dataSourceTemplates/types";
import { useTenant } from "~/features/tenant/context";
import { translateError } from "~/lib/errors";

interface UseDataSourceTemplateEditOptions {
  onSuccess?: () => void;
  onError?: (error: Pick<ValidationError, "fields">) => void;
}

export function useDataSourceTemplateEdit({ onSuccess, onError }: UseDataSourceTemplateEditOptions = {}) {
  const { tenantId } = useTenant();
  if (!tenantId) {
    throw new Error("Tenant ID is required");
  }
  const { t } = useTranslation();
  const [processing, setProcessing] = useState(false);
  const [updateDataSourceTemplate] = useMutation<
    UpdateDataSourceTemplateMutation,
    UpdateDataSourceTemplateMutationVariables
  >(UpdateDataSourceTemplateDocument);

  const submit = useCallback(
    async (formData: DataSourceTemplateFormData) => {
      if (!formData.id) return;
      const id = formData.id;
      setProcessing(true);
      try {
        const res = await updateDataSourceTemplate({
          refetchQueries: [
            { query: GetDataSourceTemplateForEditFormDocument, variables: { tenantId, id } },
            { query: GetDataSourceTemplatesDocument, variables: { tenantId } },
            { query: GetDataSourceTemplateDetailDocument, variables: { tenantId, dataSourceTemplateId: id } },
          ],
          variables: {
            input: {
              tenantId,
              id,
              name: formData.name,
              propertySchemaAssociations: (formData.propertySchemaAssociations ??
                []) as unknown as PropertySchemaAssociationInput[],
            },
          },
        });

        match(res.data?.cnutUpdateDataSourceTemplate)
          .with({ __typename: "ValidationError" }, (e) => onError?.(e))
          .with({ __typename: "DataSourceTemplateUpdateOutput" }, () => {
            toast.success(t("feature.common.updatedSuccessfully", { model: "dataSourceTemplate" }));
            onSuccess?.();
          })
          .otherwise(() => {});
      } catch (e) {
        toast.error(translateError(t, e));
      } finally {
        setProcessing(false);
      }
    },
    [updateDataSourceTemplate, tenantId, t, onSuccess, onError],
  );

  return { submit, processing };
}
